import {
  Box,
  Center,
  Loader,
  Popover,
  Text,
  Tooltip,
  type BoxProps,
} from '@mantine/core';
import { Badge, Banner } from '@vrplatform/components';
import { Icon } from '@vrplatform/icons';
import { useState, type ComponentProps } from 'react';
import {
  getJournalFreshnessCopy,
  type JournalStatus,
} from './journalFreshness.utils';

type Props = {
  journalStatus: JournalStatus;
  failureMessage?: string;
  variant?: 'compact' | 'banner' | 'icon';
  action?: Extract<ComponentProps<typeof Banner>, { title: string }>['action'];
  owner?: boolean;
  subject?: 'figures' | 'financials' | 'statement';
  mt?: BoxProps['mt'];
  mb?: BoxProps['mb'];
};

export const JournalFreshnessIndicator = ({
  journalStatus,
  failureMessage,
  variant = 'compact',
  action,
  owner = false,
  subject = 'financials',
  mt,
  mb = 'md',
}: Props) => {
  const [opened, setOpened] = useState(false);
  // The owner portal shows no journal notices: owners cannot act on them.
  if (owner) return null;
  const { label, details, pending, failed, blockingLock } =
    getJournalFreshnessCopy({
      journalStatus,
      failureMessage,
      subject,
      hasAction: !!action,
    });
  if (!label) return null;

  if (variant === 'banner') {
    return (
      <Box mt={mt} mb={mb}>
        <Box role={pending ? 'status' : 'alert'}>
          <Banner
            mounted
            type={
              pending ? 'info' : failed || blockingLock ? 'error' : 'warning'
            }
            size="sm"
            mb={0}
            shadow={null}
            loading={pending}
            title={label}
            message={details}
            action={pending ? undefined : action}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      withinPortal
      width={300}
      shadow="md"
    >
      <Popover.Target>
        <Tooltip
          label={details}
          disabled={variant !== 'icon' || opened}
          events={{ hover: true, focus: true, touch: false }}
          maw={300}
          multiline
          withArrow
        >
          <Box
            component="button"
            type="button"
            aria-label={label}
            aria-expanded={opened}
            onClick={(event) => {
              event.stopPropagation();
              setOpened((value) => !value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && opened) {
                event.stopPropagation();
                setOpened(false);
              }
            }}
            style={{
              background: 'none',
              border: 0,
              padding: 0,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {variant === 'icon' ? (
              <Center w={24} h={24}>
                {pending ? (
                  <Loader size={16} color="blue" />
                ) : (
                  <Icon
                    icon="AlertTriangleIcon"
                    size={16}
                    color={(theme) =>
                      theme.colors[failed || blockingLock ? 'red' : 'orange'][7]
                    }
                  />
                )}
              </Center>
            ) : (
              <Badge
                appearance="tag"
                size="sm"
                loading={pending}
                leftIcon={pending ? undefined : 'AlertTriangleIcon'}
                leftIconColor={failed || blockingLock ? 'red' : 'orange'}
                tooltip={details}
              >
                {label}
              </Badge>
            )}
          </Box>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown onClick={(event) => event.stopPropagation()}>
        <Text size="sm">{details}</Text>
      </Popover.Dropdown>
    </Popover>
  );
};
